/**
 * THE ONLY MODULE THAT NAMES ms-agents *REQUEST* FIELDS.
 *
 * Its sibling `broadcastDecode.js` owns response fields. Everything else in
 * the Broadcasts surface speaks the camelCase draft vocabulary from
 * `broadcastDraft.js`. If you find yourself typing `notification_title`,
 * `route_key`, `push_segment`, `email_segment`, `image_url`, `channels` or
 * `scheduled_at` anywhere else, it belongs here instead.
 *
 * ---------------------------------------------------------------------------
 * CONTRACT — ms-agents v1_10_0, POST /api/v1/broadcasts (+ /estimate)
 * ---------------------------------------------------------------------------
 * A broadcast is one title, one description, one image and one destination,
 * sent as a push, an email, or both, with a SEPARATE AUDIENCE PER CHANNEL.
 *
 *   company_id           uuid
 *   notification_title   <=60 — ALSO the email subject
 *   notification_body    <=300 — ALSO the email preheader, which the composer
 *                        derives from the body rather than asking for
 *   image_url            1200x628, <=2MB. Omitted entirely when there is none.
 *   channels             "push" | "email" | "both"
 *   route_key            home|subscriptions|workouts|food|steps|chat (closed)
 *   push_segment         required whenever push is targeted
 *   email_segment        required whenever email is targeted; accepts three
 *                        extra email-only values (all_contacts, app_users,
 *                        not_on_app) that push must never be given
 *   scheduled_at         omit/null = send now
 *
 * ---------------------------------------------------------------------------
 * THE WIRE STILL HAS ONE CONTENT SLOT. THE COMPOSER HAS TWO.
 * ---------------------------------------------------------------------------
 * `notification_title` / `notification_body` / `image_url` are the API's single
 * pair of content fields, and the API's rule is that an email falls back to
 * them when `email_subject` / `email_body_html` are absent. The composer no
 * longer works that way: a popup and an email are written separately and only
 * one of them is ever built.
 *
 * So this module FILLS THAT ONE SLOT FROM WHICHEVER MODE IS ACTIVE. A popup
 * sends its title, description and image. An email sends its subject as
 * `notification_title` and the opening words of its body as
 * `notification_body` — and repeats the subject in `email_subject` rather than
 * relying on the fallback, because a payload that states what it means does not
 * depend on the server agreeing with us about what a missing field implies.
 *
 * An email sends NO `image_url`: the composer does not offer one. `image_url`
 * is a popup field now, and the key is simply absent on an email payload.
 *
 * `route_key` is sent in both modes because the contract lists it as required.
 * On an email it is inert — the button's destination is `email_cta_url` — so
 * the composer does not offer a picker for it and the default rides along.
 *
 * 201 -> { success, id, enqueued }.  422 = validation or moderation; the
 * `detail` is rendered verbatim next to the offending field.
 *
 * ---------------------------------------------------------------------------
 * WHAT REPLACED WHAT
 * ---------------------------------------------------------------------------
 * The pre-v1_10_0 shape (`audience_type` + `audience_filter_json` +
 * `channel_mode` + `deep_link` + `attachment_type`) was reverse-engineered
 * from 422s on 2026-08-02 and is GONE — along with the dual-shape machinery
 * that existed to re-emit legacy records in their original form. One shape,
 * one builder.
 *
 * ---------------------------------------------------------------------------
 * STILL OPEN (see the Phase 0 probes)
 * ---------------------------------------------------------------------------
 *  Q1 Does `image_url` want the presign `data_url` or the `blob_path`?
 *     `data_url` is sent, matching how the chat composer treats it as the
 *     blob's durable identity.
 *  Q2 Is `email_body_html` required when `channels` includes email? Sent
 *     whenever the author wrote a body, which on an email draft is always —
 *     validation blocks an empty one.
 */
import { usesEmail, usesPush } from "./broadcastDraft";
import { BODY_MAX, segmentNeedsFilter } from "./broadcastEnums";
import { emailBodyPlainText, sanitizeEmailHtml } from "./emailBodyHtml";
import { toDatetimeLocal, toIsoDate, toScheduledAtIso } from "./broadcastTime";

// Re-exported so existing importers keep a single place to reach for these.
// They live in `broadcastTime.js` because `broadcastDraft.js` needs them too,
// and importing them from here would make a cycle.
export { toScheduledAtIso, toDatetimeLocal };

/**
 * Serialize the targeting half of the contract.
 *
 * Shared by create and estimate BY CONSTRUCTION, so the audience you estimate
 * is always the audience you send. The previous two composers each built their
 * own and drifted apart, which is how one of them came to display "0 clients"
 * for every segment.
 *
 * Only the channel actually in play contributes a segment. Sending a
 * `push_segment` on an email-only broadcast would be describing an audience
 * that receives nothing.
 *
 * @param {import("./broadcastDraft").BroadcastDraft} draft
 * @returns {{ channels: string, push_segment?: string, email_segment?: string }}
 */
const serializeTargeting = (draft) => ({
  channels: draft.channels,
  ...(usesPush(draft)
    ? {
        push_segment: draft.pushSegment,
        // Only the `filtered` segment carries an id. Sending a stale
        // `push_filter_id` alongside `active` would describe two different
        // audiences in one request.
        ...(segmentNeedsFilter(draft.pushSegment) && draft.pushFilterId
          ? { push_filter_id: draft.pushFilterId }
          : {}),
      }
    : {}),
  ...(usesEmail(draft)
    ? {
        email_segment: draft.emailSegment,
        ...(segmentNeedsFilter(draft.emailSegment) && draft.emailFilterId
          ? { email_filter_id: draft.emailFilterId }
          : {}),
      }
    : {}),
});

const text = (value) => String(value || "").trim();

/**
 * Serialize the content half: the one wire slot, filled from the active mode.
 *
 * The dormant mode contributes NOTHING. A popup draft carries no
 * `email_body_html` and an email draft carries no popup title — not an empty
 * one, none at all. Sending the other surface's leftovers is how a broadcast
 * ends up with copy nobody wrote for it.
 *
 * @param {import("./broadcastDraft").BroadcastDraft} draft
 * @returns {Object}
 */
const serializeContent = (draft) => {
  if (usesEmail(draft)) {
    const subject = text(draft.emailSubject);
    const body = sanitizeEmailHtml(draft.emailBodyHtml);
    const ctaText = text(draft.emailCtaText);
    const ctaUrl = text(draft.emailCtaUrl);

    // DERIVED, NOT ASKED FOR.
    //
    // `notification_body` is the inbox preview line, and the composer no longer
    // has a field for it. It is the body's own opening words, which is what a
    // mail client would scrape anyway — the difference is that we send it, so
    // what the author saw in the preview rail is what the inbox shows.
    //
    // Truncated at `BODY_MAX` because the wire enforces that limit and a long
    // body would otherwise fail validation for a field nobody typed into.
    const preview = emailBodyPlainText(draft.emailBodyHtml).slice(0, BODY_MAX);

    return {
      notification_title: subject,
      notification_body: preview,
      // Stated rather than inherited — see the header note.
      ...(subject ? { email_subject: subject } : {}),
      // Sanitized on the way out. The editor already writes clean markup, but
      // the wire is the boundary where that has to be true rather than likely.
      ...(body ? { email_body_html: body } : {}),
      // The pair is only meaningful together: a label with no destination is a
      // dead button, and a destination with no label renders as an empty one.
      ...(ctaText && ctaUrl
        ? { email_cta_text: ctaText, email_cta_url: ctaUrl }
        : {}),
    };
  }

  const image = text(draft.imageUrl);
  return {
    notification_title: text(draft.title),
    notification_body: text(draft.body),
    ...(image ? { image_url: image } : {}),
  };
};

/**
 * Build the request body for POST /api/v1/broadcasts.
 *
 * PURE — no localStorage, no `Date.now()`, no context reads. Every ambient
 * value arrives via `context`, which is what makes this unit-testable and the
 * shape trivially swappable.
 *
 * @param {import("./broadcastDraft").BroadcastDraft} draft
 * @param {{ companyId: string|null }} [context]
 * @returns {Object} The request body.
 * @throws {Error} When the draft is in a state the UI should have blocked.
 */
export function buildBroadcastPayload(draft, context = {}) {
  const scheduledAt =
    draft.scheduleMode === "later" ? toScheduledAtIso(draft.scheduledAtLocal) : null;

  if (draft.scheduleMode === "later" && !scheduledAt) {
    // Unreachable from a valid UI — `validateDraft` blocks Send first.
    // Throwing rather than silently degrading to `scheduled_at: null` IS the
    // point: that degradation is the bug this replaces, where the toast said
    // "scheduled" and the broadcast went out immediately.
    //
    // Checks the PARSED instant, not merely the presence of a string. A
    // truthy-but-unparseable value ("next tuesday") also yields null from
    // `toScheduledAtIso`, and presence alone would wave it straight through
    // into exactly the send-now degradation this guard exists to stop.
    throw new Error(
      "scheduleMode 'later' needs a parseable scheduledAtLocal; " +
        `got ${JSON.stringify(draft.scheduledAtLocal)}`
    );
  }

  return {
    company_id: context.companyId,
    // Inert on an email, required by the contract in both modes. See the
    // header note.
    route_key: draft.routeKey,
    ...serializeContent(draft),
    ...serializeTargeting(draft),
    // Omit to send now. The guide says "omit/null = send now", and omitting
    // says it without relying on null being read the same way.
    ...(scheduledAt ? { scheduled_at: scheduledAt } : {}),
  };
}

/**
 * Build the body for POST /api/v1/broadcasts/estimate.
 *
 * @param {import("./broadcastDraft").BroadcastDraft} draft
 * @param {{ companyId: string|null }} [context]
 * @returns {Object}
 */
export function buildEstimatePayload(draft, context = {}) {
  return {
    company_id: context.companyId,
    ...serializeTargeting(draft),
  };
}

/**
 * Turn a day count into the list endpoint's date window.
 *
 * Returns camelCase — the service maps it to the wire names — so this helper
 * stays a date calculation rather than a second place that knows the contract.
 * `now` is injected to keep it pure.
 *
 * @param {number} days
 * @param {{ now?: Date }} [options]
 * @returns {{ dateFrom: string, dateTo: string }} YYYY-MM-DD, inclusive.
 */
export function dateRangeFromDays(days, { now = new Date() } = {}) {
  const from = new Date(now.getTime());
  // `days` is a window INCLUDING today, so 7 days is today plus the six
  // before it. Subtracting the full count would quietly widen every range.
  from.setDate(from.getDate() - (Number(days) - 1));
  // Formatted from the LOCAL calendar to match the local `setDate` walk above.
  // `toISOString().slice(0,10)` reads the UTC calendar, and mixing the two
  // shifts the whole window by a day for anyone whose offset straddles
  // midnight UTC — see `toIsoDate`.
  return { dateFrom: toIsoDate(from), dateTo: toIsoDate(now) };
}
